import React from 'react';
import {
    hasModifier,
    isIgnorableKeydown,
    type ShortcutScope,
    useIsShortcutScopeActive,
} from '@/components/shortcuts';
import type { PinPadAction } from './pinPadMeta';

const FLASH_DURATION_MS = 150;
const DIGIT_PATTERN = /^[0-9]$/;

export type UsePinPhysicalKeysOptions = {
    /**
     * The scope of the screen or dialog showing this pad, pushed (via
     * `useShortcutScope`) by its owner: `SELLER_LOGIN_SCOPE` for the whole
     * sign-in screen, `SHIFT_SCOPE` for the shift dialog's co-sign. Keys are
     * ignored unless it is the live top of the shortcut stack, so anything
     * opened on top (the cheat sheet, for one) suspends physical entry the
     * same way it suspends every other shortcut.
     */
    scope: ShortcutScope;
    /** Mirrors the pad's own `isDisabled` (mid-verify, or held for a lockout/rate limit): digits and backspace do nothing, matching the on-screen pad. */
    isDisabled: boolean;
    onDigit: (digit: string) => void;
    onBackspace: () => void;
    /** Always allowed while this scope is live — the caller decides what Escape does (clear the entry, or back out). */
    onEscape: () => void;
};

/**
 * Feeds a `PinPad` from the physical keyboard: `0`-`9` call `onDigit`,
 * `Backspace` calls `onBackspace`, `Esc` calls `onEscape` — the same input
 * path the on-screen keys already use. A held key enters one digit
 * (`event.repeat` is ignored) and a modified key does nothing at all, so
 * `Ctrl+1` never types a `1`. Returns the key whose on-screen twin should
 * flash for a moment, so the physical and on-screen inputs read as one.
 *
 * Its own `keydown` listener rather than a `useShortcut` registration — see
 * `SELLER_LOGIN_SCOPE`'s comment in `keymap.ts` for why; the registry's
 * display-only PIN entries list these keys. It stays scope-aware even so:
 * it only acts while `scope` is the live top of the shortcut stack
 * (`useIsShortcutScopeActive`), so it suspends the same way every other
 * binding does when a dialog opens on top.
 */
export const usePinPhysicalKeys = ({
    scope,
    isDisabled,
    onDigit,
    onBackspace,
    onEscape,
}: UsePinPhysicalKeysOptions): string | PinPadAction | null => {
    const isScopeActive = useIsShortcutScopeActive(scope);
    const [flashedKey, setFlashedKey] = React.useState<
        string | PinPadAction | null
    >(null);
    const flashTimeoutRef = React.useRef<
        ReturnType<typeof setTimeout> | undefined
    >(undefined);

    const isDisabledRef = React.useRef(isDisabled);
    isDisabledRef.current = isDisabled;
    const onDigitRef = React.useRef(onDigit);
    onDigitRef.current = onDigit;
    const onBackspaceRef = React.useRef(onBackspace);
    onBackspaceRef.current = onBackspace;
    const onEscapeRef = React.useRef(onEscape);
    onEscapeRef.current = onEscape;

    const flash = React.useCallback((key: string | PinPadAction) => {
        clearTimeout(flashTimeoutRef.current);
        setFlashedKey(key);
        flashTimeoutRef.current = setTimeout(
            () => setFlashedKey(null),
            FLASH_DURATION_MS,
        );
    }, []);

    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (isIgnorableKeydown(event) || hasModifier(event)) {
                return;
            }
            if (!isScopeActive()) {
                return;
            }
            if (DIGIT_PATTERN.test(event.key)) {
                event.preventDefault();
                if (isDisabledRef.current) {
                    return;
                }
                flash(event.key);
                onDigitRef.current(event.key);
            } else if (event.key === 'Backspace') {
                event.preventDefault();
                if (isDisabledRef.current) {
                    return;
                }
                flash('backspace');
                onBackspaceRef.current();
            } else if (event.key === 'Escape') {
                event.preventDefault();
                onEscapeRef.current();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isScopeActive, flash]);

    React.useEffect(() => () => clearTimeout(flashTimeoutRef.current), []);

    return flashedKey;
};
